import { GridColumn as Column } from '@progress/kendo-react-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import { BooleanCell } from 'src/components/shared/DataGrid/BooleanCell';
import { BooleanFilterCell } from 'src/components/shared/DataGrid/BooleanFilterCell';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { GridCountCell } from 'src/components/shared/DataGrid/GridCountCell';
import {
  HeaderSection,
  HeaderSectionLeft,
  HeaderSectionRight
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useReduxDispatch } from 'src/store';
import { GradeDetailDto, GradeDto } from 'src/store/grades/grade';
import { mapGradeDetailToEdit } from 'src/store/grades/grade.map';
import {
  createGrade,
  deleteGrade,
  editGrade,
  fetchGrades,
  GradeTypes
} from 'src/store/grades/grades.actions';
import { GradeApi } from 'src/store/grades/grades.api';
import { selectGrades } from 'src/store/grades/grades.selectors';
import { fetchInstruments } from 'src/store/instruments/instruments.actions';
import { isLoadingGrades } from 'src/store/requests/requests.selectors';
import { GradeForm } from './Grade/GradeForm';

export const GradesPage = () => {
  const [currentGrade, setCurrentGrade] = useState<GradeDto | null>(null);
  const [grade, setGrade] = useState<Nullable<GradeDetailDto>>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  const { t } = useTranslation();

  const dispatch = useReduxDispatch();

  const grades = useSelector(selectGrades);
  const loadingGrades = useSelector(isLoadingGrades);

  useEffect(() => {
    dispatch(fetchInstruments());
    dispatch(fetchGrades());
  }, [dispatch]);

  useEffect(() => {
    if (currentGrade) {
      setShowDeleteModal(true);
    }
  }, [currentGrade]);

  const onCancel = () => {
    setCurrentGrade(null);
    setGrade(null);
    setShowDeleteModal(false);
    setShowEditModal(false);
  };

  const onCreate = () => {
    setShowEditModal(true);
  };

  const onEdit = async (grade: GradeDto) => {
    const response = await GradeApi.getById(grade.id);
    if (response.ok) {
      setGrade(response.data);
      setShowEditModal(true);
    }
  };

  const onDelete = (grade: GradeDto) => {
    setCurrentGrade(grade);
  };

  const onConfirmDelete = () => {
    if (currentGrade) {
      dispatch(deleteGrade(currentGrade.id, currentGrade));
      setCurrentGrade(null);
      setShowDeleteModal(false);
    }
  };

  const onSubmit = async (grade: GradeDetailDto) => {
    const dto = mapGradeDetailToEdit(grade);
    const result = dto.id
      ? await dispatch(editGrade(dto.id, dto))
      : await dispatch(createGrade(dto));

    if (result.type !== GradeTypes.Failure) {
      dispatch(fetchGrades());
      onCancel();
    }
  };

  return (
    <>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('grade.plural')}</Header1>
          <BodyType>{`${t('crudtext')} ${t('grade.plural')}`}</BodyType>
        </HeaderSectionLeft>
        <HeaderSectionRight>
          <SecondaryButton onClick={onCreate} icon="la-plus">
            {`${t('create.verb')} ${t('grade.singular')}`}
          </SecondaryButton>
        </HeaderSectionRight>
      </HeaderSection>

      <DataGrid
        items={grades}
        loading={loadingGrades}
        onRowClick={({ dataItem }) => onEdit(dataItem)}
        pageable
        pageSize={20}
        initialSort={[{ field: 'no', dir: 'asc' }]}
      >
        <Column
          field="no"
          width={200}
          title={t('grade.number')}
          footerCell={() => (
            <GridCountCell count={grades?.length} label={t('grade.plural')} />
          )}
        />
        <Column field="name" title={t('props.name')} />
        <Column field="description" title={t('props.description')} />
        <Column
          width="160px"
          field="active"
          title={t('props.active')}
          cell={props => <BooleanCell {...props} />}
          filterCell={props => <BooleanFilterCell {...props} />}
        />
        <Column
          width="160px"
          field="corrected"
          title={t('grade.corrected')}
          cell={props => <BooleanCell {...props} />}
          filterCell={props => (
            <BooleanFilterCell
              trueLabel={t('props.true')}
              falseLabel={t('props.false')}
              {...props}
            />
          )}
        />
        <Column
          width="160px"
          field="internal"
          title={t('props.internal')}
          cell={props => <BooleanCell {...props} />}
          filterCell={props => (
            <BooleanFilterCell
              trueLabel={t('props.true')}
              falseLabel={t('props.false')}
              {...props}
            />
          )}
        />
        <Column
          width="100px"
          filterable={false}
          cell={props => (
            <td>
              <DeleteButton onClick={() => onDelete(props.dataItem)} />
            </td>
          )}
        />
      </DataGrid>

      <Modal
        header={`${grade ? t('edit') : t('create.verb')} ${t(
          'grade.singular'
        )}`}
        confirmText={t('save.verb')}
        onClose={() => onCancel()}
        open={showEditModal}
        width={864}
      >
        <GradeForm grade={grade} onCancel={onCancel} onSubmit={onSubmit} />
      </Modal>

      <Modal
        width={500}
        header={`${t('delete.verb')} ${t('grade.singular')}`}
        confirmText={t('delete.verb')}
        onClose={() => onCancel()}
        onConfirm={() => onConfirmDelete()}
        open={showDeleteModal}
        showActions
      >
        <BodyType>
          {t('grade.delete')} <q>{currentGrade?.name}</q>?
        </BodyType>
      </Modal>
    </>
  );
};
